import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import { api } from '@/services/api';
import { LeaderboardEntry } from '@/types';
import { ChevronLeft, Trophy } from 'lucide-react';

const LEVEL_COLORS: Record<string, string> = {
  'Новичок': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  'Игрок': 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  'Опытный': 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  'Ветеран': 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400',
  'Мастер': 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400',
  'Легенда': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
};

function getInitials(firstName?: string, username?: string): string {
  const name = firstName || username || '?';
  return name.slice(0, 2).toUpperCase();
}

function getRankEmoji(rank: number): string {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
}

export function LeaderboardPage() {
  const navigate = useNavigate();
  const { user } = useStore();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getLeaderboard(50)
      .then(setEntries)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const myEntry = entries.find(e => e.user_id === user?.telegram_id);

  return (
    <div className="pb-24 max-w-2xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 pt-6 pb-8">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate(-1)} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
            <ChevronLeft size={24} />
          </button>
          <Trophy size={24} />
          <h1 className="text-2xl font-bold">Рейтинг игроков</h1>
        </div>
        <p className="text-purple-100 text-sm ml-10">Топ-50 по очкам опыта</p>
      </div>

      {/* Current user rank banner (if not in top list) */}
      {myEntry && (
        <div className="mx-4 -mt-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 mb-4 border-2 border-purple-400 dark:border-purple-600">
          <p className="text-xs text-purple-500 font-semibold uppercase tracking-wide mb-1">Ваша позиция</p>
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-gray-500 w-8 text-center">
              {getRankEmoji(myEntry.rank)}
            </span>
            <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-sm">
              {getInitials(myEntry.first_name, myEntry.username)}
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 dark:text-white">
                {myEntry.first_name || myEntry.username || 'Вы'}
              </p>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${LEVEL_COLORS[myEntry.level_name] ?? LEVEL_COLORS['Новичок']}`}>
                {myEntry.level_name}
              </span>
            </div>
            <span className="text-purple-600 dark:text-purple-400 font-bold">
              {myEntry.xp.toLocaleString('ru-RU')} XP
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400 px-4">
          <Trophy className="mx-auto mb-3 text-gray-300" size={48} />
          <p>Пока никто не набрал очков.</p>
          <p className="text-sm mt-1">Участвуй в розыгрышах — зарабатывай XP первым!</p>
        </div>
      ) : (
        <div className="px-4 mt-4 space-y-2">
          {entries.map((entry) => {
            const isMe = entry.user_id === user?.telegram_id;
            return (
              <div
                key={entry.user_id}
                className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                  isMe
                    ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-300 dark:border-purple-700'
                    : 'bg-white dark:bg-gray-800'
                }`}
              >
                {/* Rank */}
                <span className="text-base font-bold text-gray-400 w-8 text-center flex-shrink-0">
                  {getRankEmoji(entry.rank)}
                </span>
                {/* Avatar */}
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${
                  isMe ? 'bg-purple-500' : 'bg-gradient-to-br from-blue-400 to-purple-500'
                }`}>
                  {getInitials(entry.first_name, entry.username)}
                </div>
                {/* Name + level */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                    {entry.first_name || entry.username || `User ${entry.user_id}`}
                    {isMe && <span className="ml-1 text-xs text-purple-500">(вы)</span>}
                  </p>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${LEVEL_COLORS[entry.level_name] ?? LEVEL_COLORS['Новичок']}`}>
                    {entry.level_name}
                  </span>
                </div>
                {/* XP */}
                <span className="text-sm font-bold text-gray-700 dark:text-gray-200 flex-shrink-0">
                  {entry.xp.toLocaleString('ru-RU')} XP
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
